/**
 * Contexto de autenticação
 * Gerencia o estado de autenticação do usuário em toda a aplicação
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import { login as loginService, register as registerService, logout as logoutService } from '../services/authService';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Carrega dados do usuário do localStorage ao iniciar
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      const parsedUser = JSON.parse(storedUser);
      if (parsedUser && typeof parsedUser.permissoes === 'string') {
        try { parsedUser.permissoes = JSON.parse(parsedUser.permissoes); } catch { parsedUser.permissoes = {}; }
      }
      setUser(parsedUser);
    }

    setLoading(false);
  }, []);

  /**
   * Realiza o login do usuário
   */
  const login = async (email, senha) => {
    console.log('🟢 [AuthContext] login() chamado');
    console.log('📧 Email:', email);
    
    try {
      console.log('🌐 [AuthContext] Chamando loginService...');
      const response = await loginService(email, senha);
      
      console.log('📦 [AuthContext] Resposta do loginService:', response);
      
      // O interceptor já retorna response.data, então response JÁ É o objeto { success, token, user }
      const { token, user } = response;

      // Garante que permissoes é sempre objeto, nunca string JSON
      if (user && typeof user.permissoes === 'string') {
        try { user.permissoes = JSON.parse(user.permissoes); } catch { user.permissoes = {}; }
      }

      console.log('🔑 [AuthContext] Token recebido:', token ? 'SIM' : 'NÃO');
      console.log('👤 [AuthContext] User recebido:', user);

      setToken(token);
      setUser(user);
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      console.log('✅ [AuthContext] Login realizado com sucesso!');
      return { success: true };
    } catch (error) {
      console.error('❌ [AuthContext] Erro no login:', error);
      console.error('❌ [AuthContext] Erro response:', error.response);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erro ao fazer login' 
      };
    }
  };

  /**
   * Registra um novo usuário
   */
  const register = async (dadosUsuario) => {
    try {
      const response = await registerService(dadosUsuario);
      
      // O interceptor já retorna response.data
      const { token, user } = response;
      
      setToken(token);
      setUser(user);
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { success: true };
    } catch (error) {
      console.error('Erro no registro:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erro ao registrar' 
      };
    }
  };

  /**
   * Realiza o logout do usuário
   */
  const logout = async () => {
    try {
      await logoutService();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        setUser,
        token, 
        loading, 
        login, 
        register, 
        logout,
        isAuthenticated: !!token 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook para usar o contexto de autenticação
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  
  return context;
};
